import { useEffect, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import * as HeroIcons20 from '@heroicons/react/20/solid'
import * as HeroIcons24 from '@heroicons/react/24/outline'
import { useInjection } from '@core/providers/inversify-react.provider'
import { CommandPalettePresenter } from './command-palette.presenter'

const getIcon20 = (name: string | undefined, className: string) => {
  if (!name) return null
  const Icon = HeroIcons20[name as keyof typeof HeroIcons20]
  return Icon ? <Icon aria-hidden="true" className={className} /> : null
}

const getIcon24 = (name: string | undefined, className: string) => {
  if (!name) return null
  const Icon = HeroIcons24[name as keyof typeof HeroIcons24]
  return Icon ? <Icon aria-hidden="true" className={className} /> : null
}

const KBD = 'rounded border border-shell-border px-1 font-sans'

// Mounted once inside ShellComponent, alongside TopBarComponent — renders
// nothing while closed, so it costs nothing on pages that never open it.
export const CommandPaletteComponent = observer(() => {
  const presenter: CommandPalettePresenter = useInjection(CommandPalettePresenter)
  const inputRef = useRef<HTMLInputElement>(null)
  const vm = presenter.viewModel

  // All keyboard control lives on one window-level listener rather than the
  // input's own onKeyDown: clicking a result or a quick-access link moves DOM
  // focus onto that button (nothing traps focus back to the input), and a
  // handler scoped to the input stops seeing arrows/enter/escape the moment
  // that happens. ⌘K/Ctrl+K toggles even while the palette is closed, so it
  // has to be here regardless.
  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        presenter.toggle()
        return
      }

      if (!presenter.isOpen) return

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        presenter.moveActive(1)
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        presenter.moveActive(-1)
      } else if (event.key === 'Enter') {
        event.preventDefault()
        presenter.selectActive()
      } else if (event.key === 'Escape') {
        event.preventDefault()
        presenter.close()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [presenter])

  useEffect(() => {
    if (vm.isOpen) inputRef.current?.focus()
  }, [vm.isOpen])

  if (!vm.isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[10vh]">
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm"
        onClick={() => presenter.close()}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={vm.placeholder}
        className="relative flex w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-shell-surface/90 shadow-2xl outline-1 outline-shell-border backdrop-blur-md"
      >
        <div className="grid grid-cols-1 border-b border-shell-border">
          <input
            ref={inputRef}
            type="text"
            value={vm.query}
            onChange={(event) => presenter.setQuery(event.currentTarget.value)}
            placeholder={vm.placeholder}
            aria-label={vm.placeholder}
            className="col-start-1 row-start-1 h-12 w-full bg-transparent pl-11 pr-4 text-base text-shell-ink outline-none placeholder:text-shell-ink-muted"
          />
          <MagnifyingGlassIcon
            aria-hidden="true"
            className="pointer-events-none col-start-1 row-start-1 ml-4 size-5 self-center text-shell-ink-muted"
          />
        </div>

        {vm.showEmpty ? (
          <p className="px-6 py-8 text-center text-sm text-shell-ink-muted">{vm.emptyLabel}</p>
        ) : (
          <div className="flex divide-x divide-shell-border">
            <ul className="scroll-thin max-h-96 min-w-0 flex-auto overflow-y-auto p-2">
              {vm.groups.map(
                (group) =>
                  (group.items.length > 0 || group.showLoading) && (
                    <li key={group.key}>
                      <p className="px-2 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-shell-ink-muted">
                        {group.groupLabel}
                      </p>
                      {group.showLoading && group.items.length === 0 && (
                        <p className="px-2 pb-2 text-xs text-shell-ink-muted">{vm.searchingLabel}</p>
                      )}
                      <ul>
                        {group.items.map((item) => (
                          <li key={item.key}>
                            <button
                              type="button"
                              onClick={item.select}
                              onMouseEnter={() => presenter.setActiveIndex(item.index)}
                              className={
                                item.active
                                  ? 'flex w-full items-center gap-x-3 rounded-md bg-shell-accent px-3 py-2 text-left text-sm/6 font-medium text-white'
                                  : 'flex w-full items-center gap-x-3 rounded-md px-3 py-2 text-left text-sm/6 text-shell-ink hover:bg-shell-surface-muted'
                              }
                            >
                              {getIcon20(
                                item.preIcon,
                                `size-4 shrink-0 ${item.active ? 'text-white' : 'text-shell-ink-muted'}`,
                              )}
                              <span className="truncate">{item.label}</span>
                              {item.newsCount !== undefined && (
                                <span
                                  className={`ml-auto shrink-0 rounded-full px-2 py-0.5 text-xs ${
                                    item.active
                                      ? 'bg-white/20 text-white'
                                      : 'bg-shell-surface-muted text-shell-ink-secondary'
                                  }`}
                                >
                                  {item.newsCount}
                                </span>
                              )}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ),
              )}
            </ul>

            {vm.active && (
              <div className="hidden w-72 flex-none flex-col overflow-y-auto p-6 sm:flex">
                <div className="flex size-16 items-center justify-center self-center rounded-full bg-shell-surface-muted">
                  {getIcon24(vm.active.preIcon, 'size-8 text-shell-accent')}
                </div>
                <p className="mt-4 text-center text-sm font-semibold text-shell-ink">{vm.active.label}</p>

                <dl className="mt-6 space-y-3 text-sm">
                  {vm.active.path && (
                    <div>
                      <dt className="text-xs font-semibold uppercase text-shell-ink-muted">{vm.pathLabel}</dt>
                      <dd className="mt-0.5 break-all font-mono text-xs text-shell-ink-secondary">{vm.active.path}</dd>
                    </div>
                  )}
                  {vm.active.newsCount !== undefined && (
                    <div>
                      <dt className="text-xs font-semibold uppercase text-shell-ink-muted">{vm.unreadLabel}</dt>
                      <dd className="mt-0.5 text-shell-ink-secondary">{vm.active.newsCount}</dd>
                    </div>
                  )}
                  {vm.active.isExternal && <p className="text-xs text-shell-ink-muted">{vm.externalLabel}</p>}
                </dl>
              </div>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-shell-border bg-shell-surface-muted px-4 py-2.5">
          <span className="text-xs font-semibold uppercase text-shell-ink-muted">{vm.quickAccessLabel}</span>
          {vm.quickLinks.map((link) => (
            <button
              key={link.key}
              type="button"
              onClick={link.select}
              className="flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium text-shell-ink-secondary hover:bg-shell-surface hover:text-shell-accent"
            >
              {getIcon20(link.preIcon, 'size-3.5')}
              {link.label}
            </button>
          ))}

          <span className="ml-auto flex items-center gap-x-3 text-xs text-shell-ink-muted">
            <span className="flex items-center gap-x-1">
              <kbd className={KBD}>↑↓</kbd>
              {vm.navigateHint}
            </span>
            <span className="flex items-center gap-x-1">
              <kbd className={KBD}>↵</kbd>
              {vm.selectHint}
            </span>
            <span className="flex items-center gap-x-1">
              <kbd className={KBD}>esc</kbd>
              {vm.closeHint}
            </span>
          </span>
        </div>
      </div>
    </div>
  )
})
